import React from 'react';
import { Button } from '@alefeducation/ui-elements-library-core';
import {
  ExclamationCircle,
  Pdf,
  TimesCircle,
  TrashSolid,
} from '@alefeducation/ui-elements-library-icons';
import styled from 'styled-components';

export type FileStatus = 'in-progress' | 'complete' | 'error';

interface FileStatusBarProps {
  fileName: string;
  status?: FileStatus;
  progress?: number;
  errorMessage?: string;
  onDelete: () => void;
  onRetry?: () => void;
}

const FileStatusBar: React.FC<FileStatusBarProps> = ({
  fileName,
  status,
  progress = 0,
  errorMessage = 'Unable to process, please try again',
  onDelete,
  onRetry,
}) => {
  return (
    <FileBarStatus
      isError={status === 'error'}
      isComplete={status === 'complete'}
    >
      <FileContent>
        <FileInfo>
          <Pdf width={18} height={18} />
          <FileName className="file-status-bar__file-name">{fileName}</FileName>
        </FileInfo>
        {status === 'complete' ? (
          <TrashSolid
            data-testid="file-status-delete"
            width={14}
            height={16}
            color="primary.40"
            onClick={onDelete}
          />
        ) : (
          <TimesCircle
            data-testid="file-status-delete"
            width={16}
            height={16}
            color="primary.40"
            onClick={onDelete}
          />
        )}
      </FileContent>
      {status !== 'complete' && (
        <FileStatusContainer>
          <FileBarProgress
            isProgress={status === 'in-progress'}
            isError={status === 'error'}
            style={{ width: status === 'error' ? '100%' : `${progress}%` }}
          />
        </FileStatusContainer>
      )}
      {status === 'error' && onRetry && (
        <FileBarActions>
          <div className="error-message">
            <ExclamationCircle width={14} height={14} color="error.100" />
            <p>{errorMessage}</p>
          </div>

          <Button
            variant="secondary"
            onClick={onRetry}
            className="retry-button"
          >
            Try Again
          </Button>
        </FileBarActions>
      )}
    </FileBarStatus>
  );
};

export default FileStatusBar;

const FileContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }: any) => theme.colors.primary[100]};

  svg {
    cursor: pointer;
  }
`;

const FileBarStatus = styled.div<{ isComplete: boolean; isError: boolean }>`
  border: 1px solid ${({ theme }: any) => theme.colors.primary[40]};
  border-radius: 8px;
  padding: ${(props) => (props.isComplete ? '10px 16px' : '10px 16px 16px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${(props) => (props.isError ? props.theme.colors.error[100] : '')};
`;

const FileBarProgress = styled.div<{ isProgress: boolean; isError: boolean }>`
  height: 10px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.isProgress
      ? props.theme.colors.interactive[100]
      : props.theme.colors.error[100]};
  transition: ${(props) => (props.isProgress ? 'all 0.2s linear' : '')};
`;

const FileStatusContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }: any) => theme.colors.primary[20]};

  svg {
    cursor: pointer;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileName = styled.span`
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  color: ${({ theme }: any) => theme.colors.primary[100]};
  line-height: normal;
`;

const FileBarActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .error-message {
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ theme }: any) => theme.colors.error[140]};
    font-size: 12px;
    font-weight: 300;
  }

  .retry-button {
    padding: 8px 16px;
  }
`;
